import _ from 'lodash'
import { useMemo, useState } from 'react'
import { Navigate, Outlet, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { blueGrey } from '@mui/material/colors'
import { Box, Container, Divider, IconButton, Paper, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import PublicIcon from '@mui/icons-material/Public'
import MenuIcon from '@mui/icons-material/Menu'

import PageContainer from '@/components/ui/containers/PageContainer'
import QueryBoundary from '@/components/ui/template/QueryBoundary'
import TabPanel from '@/components/ui/tab/TabPanel'

import { NetworkMember, NetworkNotificationFrequences, NetworkNotificationPreferences } from '@/core/models/network.model'
import { useGetMyNetworkInvitationsQuery } from '@/core/services/auth'
import { useGetNetworkMembersQuery, useGetNetworkQuery, useReplyNetworkInvitationMutation, useUpdateNetworkMemberMutation } from '@/core/services/network'

import { useIsWebView } from '@/hooks/useSizeBreakpoints'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import CancelJoinRequestButton from './components/CancelJoinRequestButton'
import ConsultationOrders from './containers/ConsultationOrders'
import ExpertiseOrders from './containers/ExpertiseOrders'
import InviteButtonMenu from './components/InviteButtonMenu'
import JoinNetworkButton from './components/JoinNetworkButton'
import LeaveNetworkButton from './components/LeaveNetworkButton'
import MemberRequests from './containers/MemberRequests'
import MembersTable from './containers/MembersTable'
import MoreActionMenu from './components/MoreActionMenu'
import NetworkCoverPhoto from './containers/NetworkCoverPhoto'
import NetworkDrawer from './components/NetworkDrawer'
import NetworkSettings from './containers/NetworkSettings'
import MemberSchedulesTable from './containers/MemberSchedulesTable'
import NotificationSettingsMenu from './components/NotificationSettingsMenu'
import NetworkWaitingRooms from './containers/NetworkWaitingRooms'
import { useNetworkMenuItems } from './hooks/useNetworkMenuItems'
import NetworkCreateWaitingRoom from './containers/NetworkCreateWaitingRoom'
import WaitingRoomConsultations from './containers/WaitingRoomConsultations'
import NetworkPlannings from './containers/NetworkPlannings'
import { useNetworkMemberPermissions } from './hooks/useNetworkMemberPermissions'
import NetworkChat from './containers/NetworkChat'
import NetworkActConfig from './containers/NetworkActConfig'
import NetworkDesmosConfig from './containers/NetworkDesmosConfig'
import NetworkTimeTracking from './containers/NetworkTimeTracking'
import { NetworkDailyOverview } from './components/NetworkDailyOverview'

export default function NetworkPage() {
  const navigate = useNavigate()
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isWebView = useIsWebView()
  const theme = useTheme()
  const isMobileNav = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const authUser = useAppSelector(selectAuthUser)

  const { data: network, error: getNetworkError, isLoading: isFetchingNetwork } = useGetNetworkQuery(networkId ?? skipToken)
  const { data: networkInvitations } = useGetMyNetworkInvitationsQuery({ skip: 0 }, { refetchOnMountOrArgChange: true })
  const [replyNetworkInvitation, { isLoading: isReplyingNetworkInvitation, originalArgs: replyNetworkInvitationArgs }] = useReplyNetworkInvitationMutation()

  const [tabValue, setTabValue] = useState(0)

  const isNetworkMember = useMemo(() => !!network?.members.find((member) => member === authUser?._id), [authUser, network])
  const networkInvitation = useMemo(
    () => networkInvitations?.find((networkInvitation) => networkInvitation.network._id === networkId),
    [networkId, networkInvitations]
  )
  const { data: networkMembers } = useGetNetworkMembersQuery(networkId ?? skipToken, { refetchOnMountOrArgChange: true })
  const { isNetworkAdmin, isNetworkCreator, hasDesmosConfigured } = useNetworkMemberPermissions({
    networkId,
    networkCreatorId: network?.createdBy._id,
    members: network?.members,
  })

  const [updateNetworkMember] = useUpdateNetworkMemberMutation()

  const updateNetworkMemberNotification = (frequency: NetworkNotificationFrequences, preference: NetworkNotificationPreferences) => {
    updateNetworkMember({
      networkId: networkId ?? '',
      body: {
        notificationFrequences: frequency,
        notificationPreferences: preference,
      },
    })
  }

  const member: NetworkMember | undefined = useMemo(() => networkMembers?.find((item) => item.member._id === authUser?._id), [authUser, networkMembers])
  const menuItems = useNetworkMenuItems(network)

  const isPublic = network?.privacy === 'public'
  const tabs = useMemo(
    () =>
      _.compact([
        { id: 'overview', label: t('page.network.tab.overview.label', { defaultValue: 'Overview' }) },
        (isNetworkMember || isPublic) && { id: 'members', label: t('page.network.tab.members.label') },
        (isNetworkMember || isPublic) && { id: 'schedules', label: t('page.network.tab.schedules.label') },
        (isNetworkMember || isPublic) && {
          id: 'consultationOrders',
          label: t('page.network.tab.consultationOrders.label'),
        },
        (isNetworkMember || isPublic) && { id: 'expertiseOrders', label: t('page.network.tab.expertiseOrders.label') },
        isNetworkAdmin && { id: 'timeTracking', label: t('page.network.tab.timeTracking.label') },
      ]),
    [isNetworkAdmin, isNetworkMember, isPublic, t]
  )

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  if (!authUser) return <Navigate to="/login" />

  if (isFetchingNetwork || !!getNetworkError || !network) {
    return <QueryBoundary isLoading={isFetchingNetwork} isError={!!getNetworkError} error={getNetworkError} fallback={<NotFoundPage />} />
  }

  const isRequestingToJoinNetwork = !!network.pendingMembers.find((pendingMember) => pendingMember._id === authUser?._id)
  const size = isWebView ? '0' : '30px'

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Stack direction="row" /* flex={1} sx={{ maxHeight: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }} */>
            <NetworkDrawer menuItems={menuItems} network={network} mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
            <Box flex={1} sx={{ overflow: 'auto' }}>
              {isMobileNav && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: (t) => t.zIndex.appBar,
                    bgcolor: 'background.paper',
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <IconButton onClick={() => setMobileNavOpen(true)} aria-label={t('page.network.menu.open', { defaultValue: 'Open network menu' })}>
                    <MenuIcon />
                  </IconButton>
                  <Typography variant="subtitle2" noWrap>
                    {network.name}
                  </Typography>
                </Stack>
              )}
              <Outlet />
            </Box>
          </Stack>
        }
      >
        <Route path="desmos-config" element={<NetworkDesmosConfig />} />
        <Route path="act-config" element={<NetworkActConfig />} />
        <Route path="member-requests" element={<MemberRequests />} />
        <Route path="edit" element={<NetworkSettings />} />
        <Route path="waiting-rooms" element={<NetworkWaitingRooms />} />
        <Route path="waiting-rooms/create" element={<NetworkCreateWaitingRoom />} />
        <Route path="waiting-rooms/:waitingRoomId/consultations" element={<WaitingRoomConsultations />} />
        <Route path="plannings/*" element={<NetworkPlannings />} />
        <Route path="time-tracking" element={<NetworkTimeTracking />} />
        <Route path="chat" element={<NetworkChat />} />
        <Route
          index
          element={
            <Box>
              <NetworkCoverPhoto />
              <Container maxWidth="xl" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack spacing={1} sx={{ pl: size, pr: size }}>
                  <Stack>
                    <Stack direction="row" spacing={3} marginTop={2}>
                      <Typography variant="h2" sx={{ mt: '30px' }}>
                        {network.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" py={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {network.privacy === 'public' && <PublicIcon fontSize="small" sx={{ color: (theme) => theme.palette.grey[500] }} />}
                        {network.privacy === 'private' && <LockOpenIcon fontSize="small" sx={{ color: (theme) => theme.palette.grey[500] }} />}
                        <Box sx={{ color: (theme) => theme.palette.grey[600], fontSize: '15px', fontWeight: 400 }}>
                          {t(`page.network.privacy.${network.privacy}.primary`)}
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {isNetworkMember && (
                          <>
                            <NotificationSettingsMenu
                              onSetFrequency={(frequency) =>
                                updateNetworkMemberNotification(frequency, member?.notificationPreferences ?? NetworkNotificationPreferences.ALL)
                              }
                              onSetPreference={(preference) =>
                                updateNetworkMemberNotification(member?.notificationFrequences ?? NetworkNotificationFrequences.IMMEDIATE, preference)
                              }
                              selectedFrequency={member?.notificationFrequences ?? NetworkNotificationFrequences.IMMEDIATE}
                              selectedPreference={member?.notificationPreferences ?? NetworkNotificationPreferences.ALL}
                            />
                            <InviteButtonMenu networkId={network._id} />
                            <LeaveNetworkButton networkId={network._id} />
                          </>
                        )}
                        {!isNetworkMember && !networkInvitation ? (
                          !isRequestingToJoinNetwork ? (
                            <JoinNetworkButton network={network} />
                          ) : (
                            <CancelJoinRequestButton applicant={authUser._id} networkId={network._id} />
                          )
                        ) : null}
                      </Stack>
                    </Stack>
                    {!!networkInvitation && !isNetworkMember && (
                      <Stack
                        flex={1}
                        bgcolor={blueGrey[100]}
                        borderRadius={2}
                        direction={{ xs: 'column', lg: 'row' }}
                        alignItems={{ xs: 'start', lg: 'center' }}
                        justifyContent="space-between"
                        p={2}
                        spacing={2}
                      >
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {t('page.networks.invitationNetwork.invitedBy', {
                            inviter: `${networkInvitation.inviter.userInfo.name} ${networkInvitation.inviter.userInfo.surname}`,
                          })}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <LoadingButton
                            loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'accept'}
                            onClick={async () => {
                              try {
                                await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'accept' }).unwrap()
                                navigate(`/networks/${networkInvitation.network._id}`)
                              } catch (error) {
                                enqueueSnackbar(getApiErrorMessage(error, t('notification.network.acceptInvitation.error')), { variant: 'error' })
                              }
                            }}
                            size="small"
                            variant="contained"
                          >
                            {t('common:button.join.label')}
                          </LoadingButton>
                          <LoadingButton
                            loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'decline'}
                            onClick={async () => {
                              try {
                                await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'decline' }).unwrap()
                              } catch (error) {
                                enqueueSnackbar(getApiErrorMessage(error, t('notification.network.declineInvitation.error')), { variant: 'error' })
                              }
                            }}
                            size="small"
                            variant="outlined"
                          >
                            {t('common:button.decline.label')}
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                  <Stack>
                    <Divider />
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box flex={1} sx={[{ ml: '-40px' }, isNetworkAdmin && { maxWidth: '75%' }, !isNetworkAdmin && { mr: '-40px' }]}>
                        <Tabs value={tabValue} onChange={handleChangeTab} scrollButtons variant="scrollable">
                          {_.map(tabs, (tab) => (
                            <Tab key={tab.id} label={tab.label} />
                          ))}
                        </Tabs>
                      </Box>
                      {isNetworkAdmin && (
                        <Stack alignItems="center">
                          <MoreActionMenu networkId={network._id} />
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Container>
              <TabPanel value={tabValue} index={0}>
                <PageContainer sx={{ minHeight: '500px' }}>
                  <Stack spacing={3}>
                    <NetworkDailyOverview isNetworkAdmin={isNetworkAdmin} isNetworkMember={isNetworkMember} network={network} />
                    <Paper>
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
                      <Typography component="h2" sx={{ color: (theme) => theme.palette.grey[900], fontSize: '18px', lineHeight: '24px' }}>
                        {t('page.network.about.title')}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      className="ql-editor"
                      dangerouslySetInnerHTML={{ __html: network.description || '' }}
                      sx={{
                        color: (theme) => theme.palette.grey[900],
                        fontSize: '15px',
                        lineHeight: '24px',
                        p: '10px',
                        '& img': {
                          objectFit: 'contain',
                          maxWidth: '100%',
                          height: 'auto',
                        },
                      }}
                    />
                    </Paper>
                  </Stack>
                </PageContainer>
              </TabPanel>
              <TabPanel value={tabValue} index={1} unmountOnExit>
                <PageContainer>
                  <MembersTable />
                </PageContainer>
              </TabPanel>
              <TabPanel value={tabValue} index={2} unmountOnExit>
                <PageContainer>
                  <MemberSchedulesTable />
                </PageContainer>
              </TabPanel>
              <TabPanel value={tabValue} index={3}>
                <PageContainer>
                  <ConsultationOrders />
                </PageContainer>
              </TabPanel>
              <TabPanel value={tabValue} index={4}>
                <PageContainer>
                  <ExpertiseOrders />
                </PageContainer>
              </TabPanel>
              {isNetworkAdmin && (
                <TabPanel value={tabValue} index={5} unmountOnExit>
                  <PageContainer>
                    <NetworkTimeTracking />
                  </PageContainer>
                </TabPanel>
              )}
            </Box>
          }
        />
      </Route>
    </Routes>
  )
}
